import { DateTime } from 'luxon'
import { standardLabraDateFormat } from './flyoutConstants'
import {
  standardLabraDateFormatOptions,
  standardLabraTimeFormat,
  standardLabraTimeFormatOptions,
} from './dateFormats'

export const getExpirationFromDate = (
  date: string,
  daysToCompare = 90,
  daysLessLimit = 15
) => {
  const { dayDiff, hrsDiff, monthsDiff } = getDateDiff(date)
  const isOneDayLeft = dayDiff > 0 && dayDiff <= 1
  const isLessThanOneDayLeft = dayDiff > 0 && dayDiff < 1
  const isExpriringSoon = dayDiff > 0 && dayDiff <= daysToCompare
  const isLessThanOneMonthLeft = dayDiff > 0 && monthsDiff <= 1
  const isLessThanGivenDay = dayDiff > 0 && dayDiff <= daysLessLimit
  const isExpired = dayDiff < 0 && dayDiff <= daysToCompare
  return {
    dayDiff,
    hrsDiff,
    monthsDiff,
    isOneDayLeft,
    isLessThanOneDayLeft,
    isExpriringSoon,
    isLessThanOneMonthLeft,
    isLessThanGivenDay,
    isExpired,
  }
}

export const getDateDiff = (date: string) => {
  const dayDiff = Math.ceil(
    DateTime.fromISO(date).diff(DateTime.now(), 'days').days
  )
  const hrsDiff = Math.ceil(
    DateTime.fromISO(date).diff(DateTime.now(), 'hours').hours
  )
  const monthsDiff = Math.ceil(
    DateTime.fromISO(date).diff(DateTime.now(), 'months').months
  )

  return {
    dayDiff,
    hrsDiff,
    monthsDiff,
  }
}
export const getFormattedLabraDate = (date: string) => {
  const formattedDate = DateTime.fromISO(date).toFormat(standardLabraDateFormat)
  const formattedTime = DateTime.fromISO(date).toFormat(standardLabraTimeFormat)
  return {
    formattedDate,
    formattedTime,
  }
}

/** Shown when `convertToUserTimezone` receives null, undefined, or blank input. */
export const EMPTY_DATETIME_DISPLAY = '—'

export type ConvertToUserTimezoneOptions = {
  /** Override empty placeholder (default `—`). */
  emptyValue?: string
  /** Calendar date only in the user's zone (no time or abbreviation). */
  dateOnly?: boolean
}

/** True when the string ends with a zone offset parsers can read from ISO. */
function hasIsoOffsetOrZ(iso: string): boolean {
  return (
    /Z$/i.test(iso) ||
    /[+-]\d{2}:\d{2}(:\d{2})?$/.test(iso) ||
    /[+-]\d{4}$/.test(iso)
  )
}

/** Parse API UTC/GMT instants; strings without offset are treated as UTC. */
function parseUtcInstant(isoLike: string): Date | null {
  const trimmed = isoLike.trim()
  if (!trimmed) return null

  if (hasIsoOffsetOrZ(trimmed)) {
    const date = new Date(trimmed)
    return Number.isNaN(date.getTime()) ? null : date
  }

  const normalized = trimmed.includes('T')
    ? `${trimmed}Z`
    : `${trimmed}T00:00:00.000Z`
  const date = new Date(normalized)
  return Number.isNaN(date.getTime()) ? null : date
}

/**
 * Converts a UTC/GMT API timestamp to a human-readable string in the user's
 * IANA timezone using `Intl.DateTimeFormat` (no extra date libraries).
 *
 * @example convertToUserTimezone('2026-05-14T10:32:00Z', 'Asia/Kolkata')
 *          → "May 14, 2026 at 4:02 PM"
 */
export const convertToUserTimezone = (
  utcDateString: string | null | undefined,
  userTimezone: string,
  options?: ConvertToUserTimezoneOptions
): string => {
  const emptyValue = options?.emptyValue ?? EMPTY_DATETIME_DISPLAY
  if (utcDateString == null) return emptyValue

  const trimmed = String(utcDateString).trim()
  if (!trimmed) return emptyValue

  const timeZone = (userTimezone && String(userTimezone).trim()) || 'UTC'
  const date = parseUtcInstant(trimmed)
  if (!date) return trimmed

  try {
    if (options?.dateOnly) {
      return new Intl.DateTimeFormat('en-US', {
        timeZone,
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      }).format(date)
    }

    const datePart = new Intl.DateTimeFormat('en-US', {
      timeZone,
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    }).format(date)

    const timePart = new Intl.DateTimeFormat('en-US', {
      timeZone,
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    }).format(date)

    return `${datePart} at ${timePart}`
  } catch {
    return trimmed
  }
}

/**
 * @deprecated Prefer `convertToUserTimezone` with `useUserDisplayTimeZone`.
 * Kept for callers that expect `undefined` on empty input.
 */
export const formatIsoInstantInTimeZone = (
  isoLike: string | undefined,
  timeZone: string
): string | undefined => {
  if (isoLike == null || !String(isoLike).trim()) return undefined
  return convertToUserTimezone(isoLike, timeZone)
}

export const getSinceTime = (date?: string) => {
  if (!date) return ''
  const dateDiff = DateTime.now().diff(DateTime.fromISO(date), [
    'minutes',
    'seconds',
  ])
  const minuteDiff = Math.ceil(dateDiff.minutes)
  const secondDiff = Math.ceil(dateDiff.seconds)
  return `since ${minuteDiff} minute ${secondDiff} seconds.`
}

export const getSinceDateDay = (date?: string) => {
  if (!date) return ''
  const days = Math.ceil(
    DateTime.now().diff(DateTime.fromISO(date), ['days']).days
  )
  return `Since ${getFormattedLabraDate(date).formattedDate} (${days} days)`
}

export const getPreviousMonthDate = (timeFrame: string) => {
  const today = new Date()
  const currentYear = today.getFullYear()
  const currentMonth = today.getMonth() // 0-indexed (0 for January, 11 for December)

  if (timeFrame === '1') {
    let previousMonth = currentMonth - 1
    let previousYear = currentYear

    if (previousMonth < 0) {
      previousMonth = 11
      previousYear--
    }

    const fromDate = `${previousYear}-${String(previousMonth + 1).padStart(
      2,
      '0'
    )}-01`

    const lastDayOfPreviousMonth = new Date(
      previousYear,
      previousMonth + 1,
      0
    ).getDate()
    const toDate = `${previousYear}-${String(previousMonth + 1).padStart(
      2,
      '0'
    )}-${String(lastDayOfPreviousMonth).padStart(2, '0')}`

    return { fromDate, toDate }
  } else if (timeFrame === '6') {
    let fromMonth = currentMonth - 6
    let fromYear = currentYear

    if (fromMonth < 0) {
      fromYear--
      fromMonth += 12
    }

    const fromDate = `${fromYear}-${String(fromMonth + 1).padStart(2, '0')}-01`
    let previousMonth = currentMonth - 1
    let previousYear = currentYear

    if (previousMonth < 0) {
      previousMonth = 11
      previousYear--
    }

    const lastDayOfPreviousMonth = new Date(
      previousYear,
      previousMonth + 1,
      0
    ).getDate()
    const toDate = `${previousYear}-${String(previousMonth + 1).padStart(
      2,
      '0'
    )}-${String(lastDayOfPreviousMonth).padStart(2, '0')}`

    return { fromDate, toDate }
  } else {
    return { fromDate: '', toDate: '' }
  }
}

export const getLocalDateTime = (dateStr: string) => {
  try {
    const date = new Date(dateStr)
    const formattedDate = Intl.DateTimeFormat(
      'en-US',
      standardLabraDateFormatOptions as Intl.DateTimeFormatOptions
    ).format(date)
    const formattedTime = Intl.DateTimeFormat(
      'en-US',
      standardLabraTimeFormatOptions as Intl.DateTimeFormatOptions
    )
      .format(date)
      .replace('GMT', 'UTC')
    return {
      formattedDate,
      formattedTime,
    }
  } catch {
    return { formattedDate: '-', formattedTime: '-' }
  }
}

const MINUTE_MS = 60 * 1000
const HOUR_MS = 60 * MINUTE_MS
const DAY_MS = 24 * HOUR_MS

/** Switch from a relative "Nd" label to an absolute date after this many days. */
export const COMPACT_DATE_AFTER_DAYS = 7

/**
 * Compact relative timestamp for the chat-history list: "5m" / "3h" / "2d" for
 * the last week, then an absolute "Mon D" (with year once it's a prior year).
 * Clamps future timestamps (clock skew) to "now".
 */
export const formatCompactTime = (
  iso: string,
  now: number = Date.now()
): string => {
  if (!iso) return ''
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return ''

  const diff = Math.max(0, now - then)

  if (diff < HOUR_MS) return `${Math.max(1, Math.floor(diff / MINUTE_MS))}m`
  if (diff < DAY_MS) return `${Math.floor(diff / HOUR_MS)}h`

  const days = Math.floor(diff / DAY_MS)
  if (days <= COMPACT_DATE_AFTER_DAYS) return `${days}d`

  const date = new Date(then)
  const sameYear = date.getFullYear() === new Date(now).getFullYear()
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    ...(sameYear ? {} : { year: 'numeric' }),
  })
}

/** Whole calendar days between two timestamps (ignores time-of-day), for the
 * chat-history recency buckets ("Today" / "Yesterday" / "Previous 7 days"). */
export const calendarDaysAgo = (then: number, now: number): number => {
  const startOf = (ms: number) => {
    const d = new Date(ms)
    return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()
  }
  return Math.round((startOf(now) - startOf(then)) / DAY_MS)
}
